import { useCallback, useEffect, useRef, useState } from 'react';

// Default to an insecure loopback socket for local dev, where the portal is
// served from http://localhost and ws:// is same-scheme/allowed.
//
// A DEPLOYED portal is served over HTTPS, and browsers block an insecure ws://
// connection from an HTTPS page (mixed content). For those builds set
// REACT_APP_LABEL_SCAN_WS_URL to a secure wss:// URL whose hostname matches the
// scanner app's TLS certificate and resolves to loopback on the client machine,
// e.g. wss://scanner.example.com:8787 (DNS: scanner.example.com → 127.0.0.1).
// The value is baked in at build time, so set it in the CI/deployment env.
const DEFAULT_URL = process.env.REACT_APP_LABEL_SCAN_WS_URL ?? 'ws://localhost:8787';

const MIN_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 30000;

/**
 * Connects to the local ai-label-scan WebSocket broadcast server.
 * `onEvent` is invoked with each parsed `ScanEvent` payload.
 * `onError` is invoked with a user-facing message string when the socket
 * fails to construct, errors on the wire, or receives a malformed payload.
 * Reconnects with exponential backoff and reports a `status` of
 * 'connecting' | 'open' | 'closed'. Also returns `send`, for issuing a
 * request to the scan app (e.g. `inventory:request`) — a no-op returning
 * false while the socket isn't open.
 */
const useScanSocket = (onEvent, onError, url = DEFAULT_URL) => {
  const [status, setStatus] = useState('connecting');
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;
  const errorRef = useRef(onError);
  errorRef.current = onError;
  const socketRef = useRef(null);

  useEffect(() => {
    let socket = null;
    let backoff = MIN_BACKOFF_MS;
    let retryTimer = null;
    let stopped = false;
    let connect;

    const reportError = (msg) => {
      errorRef.current?.(msg);
    };

    const scheduleRetry = () => {
      if (stopped) return;
      retryTimer = setTimeout(() => connect(), backoff);
      backoff = Math.min(backoff * 2, MAX_BACKOFF_MS);
    };

    connect = () => {
      setStatus('connecting');
      try {
        socket = new WebSocket(url);
      } catch (err) {
        reportError(`Scanner connection failed: ${err?.message ?? String(err)}`);
        scheduleRetry();
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        backoff = MIN_BACKOFF_MS;
        setStatus('open');
      };

      socket.onmessage = (msg) => {
        try {
          const event = JSON.parse(msg.data);
          handlerRef.current?.(event);
        } catch (err) {
          reportError(`Malformed scanner message: ${err?.message ?? String(err)}`);
        }
      };

      socket.onerror = () => {
        reportError('Scanner socket error — attempting to reconnect.');
      };

      socket.onclose = () => {
        setStatus('closed');
        scheduleRetry();
      };
    };

    connect();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        try {
          socket.close();
        } catch {
          /* ignore */
        }
      }
      socketRef.current = null;
    };
  }, [url]);

  const send = useCallback((data) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) return false;
    socketRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
    return true;
  }, []);

  return { status, send };
};

export default useScanSocket;
